import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { MargensPraticadoPerdeuLinha } from '@triade/contracts';
import { useMargensCustoPraticados } from '@/api/margens';
import { ProfitBadge } from '@/components/profit-badge';
import { formatarData, formatarMoeda } from '@/lib/format';
import { Bloco } from '../bloco';
import { Paginacao } from '../paginacao';
import { TabelaOrdenavel, type ColunaMargem } from '../tabela-ordenavel';
import { ROTULO_TIPO, type BuscaMargens } from '../tipos';
import { consultaCusto, DIRECAO_PRATICADOS, faixaPraticado, percentualComSinal, ROTULO_ORIGEM_MARCA } from './tipos';

type Campo = keyof typeof DIRECAO_PRATICADOS;

/** Itens com a marca "custo atualizado - revisar preco" aberta: praticado nao muda; o sugerido e o lucro sao de hoje (API). */
export function SecaoPraticadosPerderam({ busca, limit = 10, ativo = true }: { busca: BuscaMargens; limit?: number; ativo?: boolean }) {
  const [ordenar, setOrdenar] = useState<Campo>('delta');
  const [offset, setOffset] = useState(0);
  const consulta = useMargensCustoPraticados({ ...consultaCusto(busca), ordenar, limit, offset }, ativo);
  const dados = consulta.data;

  const ordenarPor = (campo: string) => {
    setOffset(0);
    setOrdenar(campo as Campo);
  };

  const colunas: ColunaMargem<MargensPraticadoPerdeuLinha>[] = [
    {
      id: 'item',
      rotulo: 'Item',
      campo: 'codigo',
      celula: (i) => (
        <Link
          to="/precificar/formacao"
          search={{ itemId: i.itemId, tabelaId: i.tabelaId, condicaoId: i.condicaoId }}
          data-testid="abrir-formacao-praticado"
          className="no-underline hover:underline"
        >
          <span className="tabular font-medium">{i.itemCodigo}</span> · {i.itemDescricao}
          <span className="ml-1.5 text-caption text-ink-muted">{ROTULO_TIPO[i.tipo]}</span>
        </Link>
      ),
    },
    {
      id: 'tabela',
      rotulo: 'Tabela · condição',
      celula: (i) => (
        <Link to="/precificar/lista" search={{ tabelaId: i.tabelaId }} data-testid="abrir-lista-praticado" className="text-ink-secondary no-underline hover:underline">
          {i.tabelaNome} · {i.condicaoCodigo}
        </Link>
      ),
    },
    {
      id: 'desde',
      rotulo: 'Desde',
      campo: 'desde',
      celula: (i) => (
        <span className="text-ink-secondary">
          <span className="tabular">{formatarData(i.desde)}</span>
          <span className="block text-caption text-ink-muted">{ROTULO_ORIGEM_MARCA[i.origem]}</span>
        </span>
      ),
    },
    { id: 'praticado', rotulo: 'Praticado', direita: true, mono: true, celula: (i) => formatarMoeda(i.praticado) },
    {
      id: 'sugerido',
      rotulo: 'Sugerido antes → hoje',
      direita: true,
      mono: true,
      celula: (i) => (
        <span>
          {formatarMoeda(i.sugeridoAntes)} → {formatarMoeda(i.sugeridoHoje)}
        </span>
      ),
    },
    {
      id: 'delta',
      rotulo: 'Variação',
      campo: 'delta',
      direita: true,
      mono: true,
      celula: (i) => (
        <span>
          {i.delta.trim().startsWith('-') ? formatarMoeda(i.delta) : `+${formatarMoeda(i.delta)}`}
          {i.deltaPct !== null && <span className="block text-caption text-ink-muted">{percentualComSinal(i.deltaPct)}</span>}
        </span>
      ),
    },
    {
      id: 'lucro',
      rotulo: 'Lucro hoje',
      direita: true,
      celula: (i) =>
        i.lucroHoje === null || i.metaLucro === null || i.minimoLucro === null ? (
          <span className="text-ink-disabled">—</span>
        ) : (
          <ProfitBadge lucro={i.lucroHoje} faixa={faixaPraticado(i)} meta={i.metaLucro} minimo={i.minimoLucro} compacto />
        ),
    },
  ];

  return (
    <section aria-label="Praticados que perderam margem desde que foram definidos" aria-busy={consulta.isFetching} className="rounded-2xl border border-border bg-card px-5 py-[18px]" data-testid="secao-custo-praticados">
      <div className="mb-3.5">
        <h2 className="m-0 text-title font-semibold">Praticados que perderam margem desde que foram definidos</h2>
        <div className="text-small text-ink-muted">
          Item × tabela × condição com a marca &ldquo;custo atualizado — revisar preço&rdquo; aberta. O praticado não muda; a marca some ao registrar um novo praticado ou ao dispensar.
        </div>
      </div>
      <Bloco
        carregando={consulta.isFetching}
        erro={consulta.error}
        temDados={!!dados}
        vazio={dados?.total === 0}
        mensagemVazia="Nenhum praticado perdeu margem desde que foi definido nesses filtros."
        textoCarregando="Carregando praticados…"
        onTentar={() => void consulta.refetch()}
      >
        {dados && (
          <div className="flex flex-col gap-3">
            <TabelaOrdenavel
              legenda="Praticados que perderam margem"
              linhas={dados.itens}
              colunas={colunas}
              ordenar={ordenar}
              direcao={DIRECAO_PRATICADOS[ordenar]}
              onOrdenar={ordenarPor}
              chave={(i) => `${i.itemId}-${i.tabelaId}-${i.condicaoId}`}
              testIdLinha="linha-custo-praticado"
            />
            <Paginacao offset={dados.offset} limit={limit} quantidade={dados.itens.length} total={dados.total} onMudar={setOffset} testId="contagem-custo-praticados" />
          </div>
        )}
      </Bloco>
    </section>
  );
}
